import { Triangle } from 'lucide-react'
import { SelectionOverflowIcon } from '@/components/ui/custom-icons'

/**
 * Non-interactive indicator shown on a text frame whose content overflows its bounds (solo frames, or
 * the last frame in a linked-text chain).
 *
 * **Persistent, not selection-gated.** Its one caller, `TextOverflowBadgeLayer` (visual layer),
 * renders it for every frame reporting overflow, with no selection or editing gate. It was briefly
 * gated on `isSelected || isEditing` to keep a busy spread from carrying permanent red chrome
 * (WMPCM-9070), but that hid the one case where text is genuinely LOST rather than flowed onward —
 * a chain's last frame — until someone happened to click it. What keeps the chrome quiet instead is
 * the narrowness of the trigger: overflow is only ever reported for a frame with no next link, and
 * only for a tail that actually carries ink (`hasVisibleOverset` in `shape-text-editor/helpers/visible-overset.ts`).
 *
 * Two pieces per design: a "+" tile centered on the border, and an "overflow" label inset from the
 * bottom-right corner. The tile is deliberately positioned to exactly coincide with
 * `TextLinkingLayer`'s unlinked out-port "+" (text-linking/components/out-port-plus-glyph.tsx,
 * `OutPortPlusGlyph`, centered directly on the frame's bottom border) — when a frame is both
 * overflowing and selected/hovered, the two render at identical coordinates and read as one
 * element instead of two overlapping ones. (A frame can never show the overflow badge alongside a
 * *linked* out-port — overflow requires no outgoing chain link.) That coincidence is now guaranteed rather than incidental: the out-port only
 * paints for a selected frame too.
 *
 * **Both pieces rotate as one rigid unit with the frame** — neither counter-rotates away. This used
 * to split the two: the tile turned with the frame (matching a resize handle's own border and
 * `PortSquare`'s numbered square, WMPCM-9223) while the label counter-rotated to protect its reading
 * orientation, the same treatment the chain-sequence numerals get. The label now follows the tile
 * instead, so the whole badge reads as a single rigid piece of the frame's own chrome, aligned with
 * its visual bounding box at every angle — not a hybrid of upright and rotated parts.
 *
 * Neither piece needs its own transform for this: both mount inside the frame's own
 * `rotate(frame.rotation)` container (`TextOverflowBadgeLayer`), so a plain `translateX(-50%)` (tile)
 * or bare positioning (label) already inherits it. `transform-origin` defaults to each element's own
 * center, so the ambient rotation leaves each piece's own anchor point exactly where its unrotated
 * position would have put it.
 *
 * Plain screen-px literals throughout, no zoom conversion of their own: `TextOverflowBadgeLayer`
 * projects the frame's visual box (`selectionChromeFrameOfShape`) into a rotated screen-space
 * container once, via `chromeGeometry`, and mounts this component inside it — so it never needs its
 * own counter-scale or a `visualRect` prop the way an inline, doc-space caller would.
 */
export function TextOverflowBadge() {
  return (
    <>
      <div
        className="pointer-events-none absolute flex items-center justify-center"
        style={{
          width: 24,
          height: 24,
          aspectRatio: '1 / 1',
          bottom: -12,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
        aria-label="Text is overflowing this frame"
      >
        <SelectionOverflowIcon />
      </div>
      <div
        className="pointer-events-none absolute flex items-center gap-0.5 rounded-md px-1 py-0.5 text-[8px] font-medium text-white"
        style={{
          bottom: -17,
          right: 16,
          backgroundColor: 'var(--color-page-editor-flow-text, #dc2626)',
        }}
      >
        <Triangle className="size-2 rotate-180" fill="currentColor" stroke="none" />
        overflow
      </div>
    </>
  )
}
